import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { nivelDificuldadeAdapter } from "@/data";
import { toast } from "@/hooks/use-toast";
import { NivelDificuldadeFormDialog } from "./NivelDificuldadeFormDialog";
import { NivelDificuldadeDeleteDialog } from "./NivelDificuldadeDeleteDialog";
import type { NivelDificuldade } from "@/types/tabelas-auxiliares";

export function NivelDificuldadeTable() {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<NivelDificuldade[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [selectedItem, setSelectedItem] = useState<NivelDificuldade | null>(null);

  useEffect(() => {
    loadItems();
  }, [search]);

  const loadItems = async () => {
    try {
      setIsLoading(true);
      const response = await nivelDificuldadeAdapter.list({ search });
      setItems(response.items);
    } catch (error) {
      toast({ title: "Erro", description: "Não foi possível carregar os dados.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setFormMode("create");
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const handleEdit = (item: NivelDificuldade) => {
    setFormMode("edit");
    setSelectedItem(item);
    setIsFormOpen(true);
  };

  const handleDelete = (item: NivelDificuldade) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    loadItems();
    toast({ title: "Sucesso", description: `Nível de dificuldade ${formMode === "create" ? "criado" : "atualizado"} com sucesso.` });
  };

  const handleDeleteSuccess = () => {
    setIsDeleteOpen(false);
    loadItems();
    toast({ title: "Sucesso", description: "Nível de dificuldade excluído com sucesso." });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Ordem</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id_nivel_dificuldade}>
                <TableCell className="font-mono text-sm">{item.codigo}</TableCell>
                <TableCell className="font-medium">{item.nome}</TableCell>
                <TableCell className="text-muted-foreground">{item.descricao || "-"}</TableCell>
                <TableCell>{item.ordem}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(item)}
                      disabled={item.sistema}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <NivelDificuldadeFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        mode={formMode}
        item={selectedItem}
        onSuccess={handleFormSuccess}
      />
      <NivelDificuldadeDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        item={selectedItem}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
}
